import { Injectable } from "@angular/core";
import { environment } from "../../../environments/environment";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable } from "rxjs";
import { EmpresaRpaDto } from "../../models/Empresas/empresa-rpa-dto";

export interface PendenciaEnqueueResponse {
  error?: string;
  enqueued?: number;
  added?: number;
  botId?: string;
  outboxRequestIds?: string[];
  codigosNaoEncontrados?: Array<number | string>;
  cnpjsInvalidos?: Array<number | string>;
  cnpjs?: string[];
  cnpjsEnfileirados?: string[];
  codigosEnfileirados?: string[];
}

export interface OutboxBotStatusResponse {
  botId: string;
  overallStatus: 'pending' | 'processing' | 'failed' | 'succeeded' | string;
  pending?: number;
  claimed?: number;
  running?: number;
  failed?: number;
  succeeded?: number;
  isComplete?: boolean;
}

@Injectable({ providedIn: 'root' })
export class RpaService {

  readonly serviceApi = environment.apiUrl;

  constructor(private http: HttpClient) { }

  sincronizaCadastro(cnpjCpf: string) {
    return this.http.get(`${this.serviceApi}/v1/sync?cnpj=${cnpjCpf}`);
  }
  requestCndDtniSp(cnpjCpf: string) {
    return this.http.get(`${this.serviceApi}/v1/rpa/cnd-dtni-sp?cnpjCpf=${cnpjCpf}`);
  }
  requestCndDtSp(cnpjCpf: string) {
    return this.http.get(`${this.serviceApi}/v1/rpa/cnd-dt-sp?cnpjCpf=${cnpjCpf}`);
  }
  requestTrabalhista(cnpjCpf: string) {
    return this.http.get(`${this.serviceApi}/v1/rpa/cnd-trabalhista?cnpjCpf=${cnpjCpf}`);
  }
  requestFgts(cnpj: string) {
    return this.http.get(`${this.serviceApi}/v1/rpa/cnd-fgts?cnpj=${cnpj}`);
  }
  requestMun(cnpj: string, codMun: string, inscMun: string) {
    return this.http.get(`${this.serviceApi}/v1/rpa/cnd-mun?cnpjCpf=${cnpj}&codMun=${codMun}&inscMun=${inscMun}`);
  }
  requestRpaUrlFile(objectName: string) {
    const params = new HttpParams().set('objectName', objectName);
    return this.http.get(`${this.serviceApi}/v1/rpa/get-url-file`, { params });
  }

  /** Caminho do objeto no bucket (fileUrl, objectName, rpaUrl ou fileName). */
  resolverObjectNameArquivo(arquivo: any): string {
    if (!arquivo) return '';

    const candidatos = [
      arquivo.fileUrl,
      arquivo.objectName,
      arquivo.ObjectName,
      arquivo.rpaUrl,
      arquivo.storagePath,
      arquivo.path
    ];

    for (const valor of candidatos) {
      const texto = String(valor ?? '').trim();
      if (!texto) continue;
      if (/^https?:\/\//i.test(texto)) continue;
      return texto;
    }

    return String(arquivo.fileName ?? '').trim();
  }

  extrairUrlAssinadaResposta(response: any): string | null {
    const url = response?.url ?? response?.Url ?? response?.signedUrl ?? response?.SignedUrl;
    return url ? String(url) : null;
  }
  requestRpaFiles() {
    return this.http.get(`${this.serviceApi}/v1/rpa/files`);
  }

  requestRpaFilesFiltrado(filter: {
    key: string;
    competencia?: string;
    tags?: string[];
    page?: number;
    pageSize?: number;
  }) {
    let params = new HttpParams()
      .set('Key', filter.key)
      .set('Page', String(filter.page ?? 1))
      .set('PageSize', String(filter.pageSize ?? 500));

    const competencia = String(filter.competencia ?? '').trim();
    if (competencia) {
      params = params.set('Competencia', competencia);
    }

    for (const tag of (filter.tags ?? []).map((t) => String(t ?? '').trim()).filter(Boolean)) {
      params = params.append('Tags', tag);
    }

    return this.http.get<{ items?: unknown[]; hasNext?: boolean }>(
      `${this.serviceApi}/v1/rpa/files`,
      { params }
    );
  }
  requestRpaFilesByEmpresa(id: number) {
    return this.http.get<any>(`${this.serviceApi}/v1/rpa/files/${id}`);
  }
  requestEmpresasRpa(typeCnd: string, cidade: string, competencia: string, typeCndFiltred: string, typeCndReport: string) {
    return this.http.get<EmpresaRpaDto[]>
      (`${this.serviceApi}/v1/rpa/search?typeCnd=${typeCnd}&cidade=${cidade}&competencia=${competencia}&typeCndFilter=${typeCndFiltred}&typeCndReport=${typeCndReport}`);
  }

  requestRpaSearchPendencias(
    typeCnd: string,
    competencia: string,
    typeCndFilter = 'ComEmisssao',
    tags?: string[]
  ) {
    let params = new HttpParams()
      .set('TypeCnd', typeCnd)
      .set('Competencia', competencia)
      .set('TypeCndFilter', typeCndFilter);

    for (const tag of (tags ?? []).map((t) => String(t ?? '').trim()).filter(Boolean)) {
      params = params.append('Tags', tag);
    }

    return this.http.get<EmpresaRpaDto[] | { items?: EmpresaRpaDto[]; data?: EmpresaRpaDto[] }>(
      `${this.serviceApi}/v1/rpa/search`,
      { params }
    );
  }

  /** Relatório pendência não inscrita — mesma regra do visualizador (/rpa/files + tags). */
  requestRelatorioPendenciasNaoInscrita(competencia: string, tags?: string[]) {
    const typeCndFilter = tags && tags.length > 0 ? 'Todos' : 'ComEmisssao';
    return this.requestRpaSearchPendencias(
      'CONSULTA-PENDENCIAS-NAO-INSCRITAS',
      competencia,
      typeCndFilter,
      tags
    );
  }

  normalizarListaEmpresas(response: unknown): EmpresaRpaDto[] {
    const bruto = Array.isArray(response)
      ? response
      : (response as { items?: unknown[]; data?: unknown[]; Items?: unknown[] } | null)?.items
        ?? (response as { data?: unknown[] } | null)?.data
        ?? (response as { Items?: unknown[] } | null)?.Items
        ?? [];

    return bruto.map((row) => this.normalizarEmpresaRpa(row));
  }

  private normalizarEmpresaRpa(row: unknown): EmpresaRpaDto {
    const item = row as Record<string, unknown>;
    const tagsBrutas = item['rpaTags'] ?? item['RpaTags'];
    const rpaTags = Array.isArray(tagsBrutas)
      ? tagsBrutas.map((tag) => String(tag ?? '').trim()).filter(Boolean)
      : [];

    return {
      ...(row as EmpresaRpaDto),
      codigoEmpresa: (item['codigoEmpresa'] ?? item['CodigoEmpresa']) as number | undefined,
      rpaTags,
      rpaUrl: String(item['rpaUrl'] ?? item['RpaUrl'] ?? '').trim() || undefined,
      rpaStatus: String(item['rpaStatus'] ?? item['RpaStatus'] ?? '').trim() || undefined,
      rpaCompetencia: String(item['rpaCompetencia'] ?? item['RpaCompetencia'] ?? '').trim() || undefined
    };
  }

  /** Relatório de pendência estadual (situação não inscrito/a). */
  requestPendenciaEstadualNaoInscrita(cidade: string, competencia: string) {
    const params = new HttpParams()
      .set('cidade', cidade)
      .set('competencia', competencia);
    return this.http.get<EmpresaRpaDto[]>(
      `${this.serviceApi}/v1/rpa/pendencia-estadual-nao-inscrita`,
      { params }
    );
  }

  /** Relatório de pendência estadual por código de empresa. */
  requestPendenciaEstadualNaoInscritaPorCodigo(codEmpresa: string) {
    return this.requestPendenciaEstadualNaoInscritaPorCodigos([codEmpresa]);
  }

  requestPendenciaEstadualNaoInscritaPorCodigos(codigos: string[]): Observable<PendenciaEnqueueResponse> {
    let params = new HttpParams();
    for (const codigo of codigos.map((c) => String(c ?? '').trim()).filter(Boolean)) {
      params = params.append('codigoEmpresa', codigo);
    }
    return this.http.get<PendenciaEnqueueResponse>(
      `${this.serviceApi}/v1/rpa/pendencia-estadual-nao-inscrita`,
      { params }
    );
  }

  /** Relatório de pendência estadual via upload de planilha (multipart/form-data). */
  requestPendenciaEstadualNaoInscritaPorPlanilha(planilha: File): Observable<PendenciaEnqueueResponse> {
    const formData = new FormData();
    formData.append('planilha', planilha);
    return this.http.post<PendenciaEnqueueResponse>(
      `${this.serviceApi}/v1/rpa/pendencia-estadual-nao-inscrita`,
      formData
    );
  }

  /** Relatório de pendência estadual inscrita por código de empresa. */
  requestPendenciaEstadualPorCodigo(codEmpresa: string) {
    return this.requestPendenciaEstadualPorCodigos([codEmpresa]);
  }

  requestPendenciaEstadualPorCodigos(codigos: string[]): Observable<PendenciaEnqueueResponse> {
    let params = new HttpParams();
    for (const codigo of codigos.map((c) => String(c ?? '').trim()).filter(Boolean)) {
      params = params.append('codigoEmpresa', codigo);
    }
    return this.http.get<PendenciaEnqueueResponse>(
      `${this.serviceApi}/v1/rpa/pendencia-estadual`,
      { params }
    );
  }

  /** Relatório de pendência estadual inscrita via upload de planilha (multipart/form-data). */
  requestPendenciaEstadualPorPlanilha(planilha: File): Observable<PendenciaEnqueueResponse> {
    const formData = new FormData();
    formData.append('planilha', planilha);
    return this.http.post<PendenciaEnqueueResponse>(
      `${this.serviceApi}/v1/rpa/pendencia-estadual`,
      formData
    );
  }

  /**
   * Enfileira consulta de pendência no Data Harvester a partir de planilha com coluna CNPJ.
   * tipo: inscrita | naoInscrita
   */
  requestPendenciaCnpjsPorPlanilha(
    tipo: 'inscrita' | 'naoInscrita',
    planilha: File
  ): Observable<PendenciaEnqueueResponse> {
    const formData = new FormData();
    formData.append('tipo', tipo);
    formData.append('planilha', planilha);
    return this.http.post<PendenciaEnqueueResponse>(
      `${this.serviceApi}/v1/pendencias/cnpjs`,
      formData
    );
  }

  /** Reprocessa CNPJs já rastreados gerando um CSV temporário com coluna CNPJ. */
  requestPendenciaCnpjsPorLista(
    tipo: 'inscrita' | 'naoInscrita',
    cnpjs: string[]
  ): Observable<PendenciaEnqueueResponse> {
    const linhas = ['CNPJ', ...cnpjs.map((c) => String(c ?? '').replace(/\D/g, '')).filter(Boolean)];
    const blob = new Blob([linhas.join('\n')], { type: 'text/csv;charset=utf-8' });
    const planilha = new File([blob], 'cnpjs-reprocessamento.csv', { type: 'text/csv' });
    return this.requestPendenciaCnpjsPorPlanilha(tipo, planilha);
  }

  /** Status agregado da consulta por botId (sem lista de requestIds). */
  requestPendenciaOutboxStatus(
    botId: string,
    since?: string
  ): Observable<OutboxBotStatusResponse> {
    let params = new HttpParams().set('botId', botId);
    const sinceValue = String(since ?? '').trim();
    if (sinceValue) {
      params = params.set('since', sinceValue);
    }
    return this.http.get<OutboxBotStatusResponse>(
      `${this.serviceApi}/v1/pendencias/outbox/status`,
      { params }
    );
  }

  baixarZipLoteStream(objectNames: string[]) {
    return this.http.post(`${this.serviceApi}/v1/rpa/download-lote-stream`, objectNames, {
      responseType: 'blob'
    });
  }

  /** Analisa PDFs de pendência estadual não inscrita por competência (empresa opcional). */
  analisarPendenciaEstadualNaoInscritaPdf(competencia: string, codigoEmpresa?: string) {
    let params = new HttpParams().set('competencia', competencia);
    const codigo = String(codigoEmpresa ?? '').trim();
    if (codigo) {
      params = params.set('codigoEmpresa', codigo);
    }
    return this.http.post<unknown>(
      `${this.serviceApi}/v1/rpa/pendencia-estadual-nao-inscrita/analisar-pdf`,
      {},
      { params }
    );
  }
}
